"use client"

import { useId, useEffect, useRef } from "react"
import { useField } from "@payloadcms/ui"
import type { TextFieldClientComponent } from "payload"

import { Input } from "@/components/ui/input"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Text field (admin.components.Field on every
 * `type: "text"` field).
 *
 * Plugs the storefront `<Input>` shadcn primitive into Payload's
 * `useField()` form-state engine — value flows through Payload's
 * autosave/validation/draft pipeline exactly like the stock TextField,
 * only the UI primitives change.
 *
 * Reads `field.label`, `field.required`, `field.admin?.description`,
 * `field.admin?.placeholder`, `field.maxLength` from the field config
 * Payload passes in via `clientField`.
 */
export const ShadcnTextField: TextFieldClientComponent = (props) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<string>({
    path: path ?? fieldName,
  })
  const id = useId()

  // Auto-focus the first text input that opens with an explicit
  // `admin.autoFocus = true` — matches Payload's stock TextField UX.
  const inputRef = useRef<HTMLInputElement>(null)
  const autoFocus = (field as { admin?: { autoFocus?: boolean } })?.admin?.autoFocus
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const placeholder =
    ((field as { admin?: { placeholder?: unknown } })?.admin?.placeholder as
      | string
      | undefined) ?? undefined
  const required = Boolean((field as { required?: boolean }).required)
  const maxLength = (field as { maxLength?: number })?.maxLength

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
      htmlFor={id}
    >
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={showError ? true : undefined}
        aria-describedby={errorMessage ? `${id}-err` : undefined}
        className="h-9 rounded-xl"
      />
    </FieldShell>
  )
}

export default ShadcnTextField
